import { template as template_53542b6411ec40d1b1c1d3aeae8da2e3 } from "@ember/template-compiler";
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { Button, CardContainer, Header, Label, RealmIcon } from '@cardstack/boxel-ui/components';
import { cn } from '@cardstack/boxel-ui/helpers';
import type { Icon } from '@cardstack/boxel-ui/icons';
import type RealmService from '@cardstack/host/services/realm';
import type { ComponentLike } from '@glint/template';
interface BaseContainerHeaderSignature {
    Args: {
        title: string;
        isActive?: boolean;
    };
    Element: HTMLElement;
    Blocks: {
        default: [];
    };
}
const BaseContainerHeader: TemplateOnlyComponent<BaseContainerHeaderSignature> = template_53542b6411ec40d1b1c1d3aeae8da2e3(`
    <Header
      @title={{@title}}
      class={{cn 'base-container-header' active=@isActive}}
      ...attributes
    >
      <:detail>
        {{yield}}
      </:detail>
    </Header>
    <style scoped>
      .base-container-header {
        --boxel-header-min-height: 1.56rem;
        --boxel-header-padding: var(--boxel-sp-5xs) var(--boxel-sp-xs);
        --boxel-header-background-color: var(--boxel-300);
        --boxel-header-text-color: var(--boxel-dark);
        font: 500 var(--boxel-font-xs);
        letter-spacing: var(--boxel-lsp-xl);
        text-transform: uppercase;
        height: var(
          --base-container-header-height,
          var(--boxel-header-min-height)
        );
      }
      .base-container-header.active {
        --boxel-header-background-color: var(--boxel-highlight);
        --boxel-header-text-color: var(--boxel-dark);
      }
    </style>
  `, {
    eval () {
        return eval(arguments[0]);
    }
});
interface BaseContainerSignature {
    Args: {
        isActive?: boolean;
    };
    Element: HTMLElement;
    Blocks: {
        default: [ComponentLike<BaseContainerHeaderSignature>];
    };
}
export const BaseContainer: TemplateOnlyComponent<BaseContainerSignature> = template_53542b6411ec40d1b1c1d3aeae8da2e3(`
    <CardContainer class={{cn 'base-container' active=@isActive}} ...attributes>
      {{yield (component BaseContainerHeader)}}
    </CardContainer>
    <style scoped>
      .base-container {
        border-radius: var(--code-mode-container-border-radius);
        overflow: hidden;
        overflow-wrap: break-word;
      }
      .base-container.active {
        border: 1px solid var(--boxel-400);
        box-shadow: var(--code-mode-active-box-shadow);
      }
    </style>
  `, {
    eval () {
        return eval(arguments[0]);
    }
});
interface Action {
    label: string;
    handler: (args: any) => void | Promise<void>;
    icon: Icon;
}
export interface BaseArgs {
    title: string;
    name: string | undefined;
    fileExtension: string | undefined;
    isActive: boolean;
    fileURL?: string;
}
interface BaseSignature {
    Element: HTMLElement;
    Args: BaseArgs;
    Blocks: {
    };
}
class BaseDefinitionContainer extends Component<BaseSignature> {
    @service
    private realm: RealmService;
    static{
        template_53542b6411ec40d1b1c1d3aeae8da2e3(`
    <BaseContainer @isActive={{@isActive}} ...attributes as |BaseHeader|>
      <BaseHeader
        @title={{@title}}
        @isActive={{@isActive}}
        data-test-definition-header
      >
        <div data-test-definition-file-extension>
          {{@fileExtension}}
        </div>
      </BaseHeader>
      <div class='content'>
        <div class='definition-info'>
          {{#if @fileURL}}
            {{#let (this.realm.info @fileURL) as |realmInfo|}}
              <div class='realm-info'>
                <RealmIcon class='realm-info-icon' @realmInfo={{realmInfo}} />
                <Label
                  class='realm-name'
                  data-test-definition-realm-name
                  @ellipsize={{true}}
                  @size='small'
                >in
                  {{realmInfo.name}}</Label>
              </div>
            {{/let}}
          {{/if}}
          <div data-test-definition-name class='definition-name'>{{@name}}</div>

        </div>
      </div>
    </BaseContainer>

    <style scoped>
      .content {
        display: flex;
        flex-direction: column;
        padding: var(--boxel-sp-xs);
        gap: var(--boxel-sp-xs);
      }
      .realm-info {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: var(--boxel-sp-xxxs);
      }
      .realm-info-icon {
        --boxel-realm-icon-size: var(--code-mode-realm-icon-size);
      }
      .realm-name {
        --boxel-label-font-weight: 400;
      }
      .definition-info {
        display: flex;
        flex-direction: column;
        gap: var(--boxel-sp-5xs);
      }
      .definition-name {
        font: 600 var(--boxel-font-sm);
        letter-spacing: var(--boxel-lsp-xs);
      }
    </style>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
export interface ActiveArgs {
    actions: Action[];
    infoText?: string;
}
interface ActiveSignature {
    Element: HTMLElement;
    Args: ActiveArgs;
}
const Active: TemplateOnlyComponent<ActiveSignature> = template_53542b6411ec40d1b1c1d3aeae8da2e3(`
  <div class='action-buttons'>
    {{#each @actions as |actionButton|}}
      <Button
        data-test-action-button='{{actionButton.label}}'
        class='action-button'
        @size='extra-small'
        @kind='text-only'
        {{on 'click' actionButton.handler}}
      >
        <actionButton.icon width='13' height='13' />
        {{actionButton.label}}
      </Button>
    {{/each}}
  </div>
  <div class='info-footer' data-test-definition-info-text data-test-percy-hide>
    {{@infoText}}
  </div>
  <style scoped>
    .action-buttons {
      display: grid;
      grid-auto-columns: max-content;
      gap: var(--boxel-sp-5xs);
    }
    .action-button {
      --boxel-button-min-height: 1.5rem;
      --boxel-button-padding: 0 var(--boxel-sp-5xs);
      --boxel-button-font: 600 var(--boxel-font-xs);
      justify-content: flex-start;
      gap: var(--boxel-sp-xxxs);
      align-self: flex-start;
    }
    .info-footer {
      color: var(--boxel-450);
      font: var(--boxel-font-xs);
      letter-spacing: var(--boxel-lsp-sm);
    }
  </style>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
export { Active, BaseDefinitionContainer };
